import { toast } from "../notify/notifyStore";
import type { BusinessRuleDetail } from "./businessRulesApi.types";
import { fetchTenantUiSettings } from "../tenantUi/tenantUiApi";

/**
 * Where a knowledge-graph write goes: the old route, or api-service's twin of it on the new
 * architecture (same path under `/api/v2`). Reads the tenant's settings each time, so a switch
 * applies at once.
 */
export const kgWritePath = async (path: string): Promise<string> => {
  const { oldArchitecture } = await fetchTenantUiSettings();
  return oldArchitecture ? path : path.replace(/^\/api\/(v1\/)?/, "/api/v2/");
};

/** api-service's `retry_after_seconds` on `KG_STILL_RUNNING`. */
export const KG_APPLY_WAIT_MS = 5000;

const stillApplying = (error: unknown): error is Error =>
  error instanceof Error &&
  (error as { status?: number }).status === 504 &&
  (error as { code?: string }).code === "KG_STILL_RUNNING";

/**
 * A twin's write that outlasted its wait (`504 KG_STILL_RUNNING`) is recorded and still applying,
 * so it counts as saved: the viewer is told so, and `saved()` answers a little later, when the
 * caller's reload can show it.
 */
export const kgWrite = async <T>(write: Promise<T>, saved: () => T): Promise<T> => {
  try {
    return await write;
  } catch (error) {
    if (!stillApplying(error)) throw error;
    toast(error.message, "info");
    await new Promise((resolve) => setTimeout(resolve, KG_APPLY_WAIT_MS));
    return saved();
  }
};

/** A business rule or note as saved while the twin still applies it: what was sent. */
export const savedRule = (
  id: string,
  input: { name: string; description?: string; save_and_verify?: boolean },
): BusinessRuleDetail => {
  const now = new Date().toISOString();
  return {
    id,
    workspace_id: "",
    name: input.name,
    description: input.description ?? null,
    verification_status: input.save_and_verify ? "verified" : "unverified",
    created_at: now,
    updated_at: now,
    deleted_at: null,
  };
};
